/**
 * Content Moderation Service
 * Handles flagged content data fetching
 * Mock/Real switching is handled at Service layer
 */

import { IS_MOCK, MOCK_DELAY } from '../../constants/config';
import { apiService } from '../api/ApiService';
import { mockFlaggedContent } from '../mock/mockContentModeration';
import { generateInvestigationDetail } from '../mock/mockInvestigationDetail';
import { delay } from '../mock/mockData';
import type {
  FlaggedContent,
  InvestigationDetail,
  ContentModerationFilter,
  PaginatedFlaggedContent,
} from '../../types/contentModeration';

class ContentModerationService {
  /**
   * Get paginated flagged content with optional filters
   */
  static async getFlaggedContent(
    page: number = 1,
    pageSize: number = 10,
    filter?: ContentModerationFilter
  ): Promise<PaginatedFlaggedContent> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      let filteredContent = [...mockFlaggedContent];

      // Apply content type filter
      if (filter?.contentType && filter.contentType !== 'all') {
        filteredContent = filteredContent.filter(
          (content) => content.type === filter.contentType
        );
      }

      // Apply status filter
      if (filter?.status && filter.status !== 'all') {
        filteredContent = filteredContent.filter(
          (content) => content.status === filter.status
        );
      }

      // Apply search filter
      if (filter?.search) {
        const searchLower = filter.search.toLowerCase();
        filteredContent = filteredContent.filter(
          (content) =>
            content.contentId.toLowerCase().includes(searchLower) ||
            content.missionId.toLowerCase().includes(searchLower) ||
            content.reasonForFlagging.toLowerCase().includes(searchLower)
        );
      }

      // Apply date range filter
      if (filter?.dateRange) {
        filteredContent = filteredContent.filter(
          (content) =>
            content.dateFlagged >= filter.dateRange!.start &&
            content.dateFlagged <= filter.dateRange!.end
        );
      }

      const total = filteredContent.length;
      const totalPages = Math.ceil(total / pageSize);
      const startIndex = (page - 1) * pageSize;
      const data = filteredContent.slice(startIndex, startIndex + pageSize);

      return {
        data,
        total,
        page,
        pageSize,
        totalPages,
      };
    } else {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (filter?.contentType && filter.contentType !== 'all') {
        params.append('contentType', filter.contentType);
      }
      if (filter?.status && filter.status !== 'all') {
        params.append('status', filter.status);
      }
      if (filter?.search) {
        params.append('search', filter.search);
      }

      const response = await apiService.get<{
        isSuccess: boolean;
        data: PaginatedFlaggedContent;
        message?: string;
      }>(`/content-moderation?${params.toString()}`);

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to fetch flagged content');
      }

      return response.data;
    }
  }

  /**
   * Get flagged content by ID
   */
  static async getFlaggedContentById(id: string): Promise<FlaggedContent | null> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      return mockFlaggedContent.find((content) => content.id === id) || null;
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: FlaggedContent;
        message?: string;
      }>(`/content-moderation/${id}`);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to fetch flagged content');
      }

      return response.data || null;
    }
  }

  /**
   * Get investigation detail by ID
   */
  static async getInvestigationDetail(id: string): Promise<InvestigationDetail | null> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const detail = generateInvestigationDetail(id);
      if (detail) {
        return detail;
      }
      // Fallback: try to find basic content
      const content = mockFlaggedContent.find(
        (item) => item.id === id || item.contentId === id || item.taskId === id
      );
      if (content) {
        return {
          ...content,
        };
      }
      return null;
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: InvestigationDetail;
        message?: string;
      }>(`/content-moderation/${id}/detail`);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to fetch investigation detail');
      }

      return response.data || null;
    }
  }

  /**
   * Update investigation internal notes
   */
  static async updateInternalNotes(id: string, notes: string): Promise<boolean> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      return true;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/content-moderation/${id}/notes`, { notes });

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to update internal notes');
      }

      return true;
    }
  }
}

export { ContentModerationService };

